import {
  color,
  deltaTime,
  float,
  Fn,
  hash,
  If,
  instancedArray,
  instanceIndex,
  min,
  time,
  uint,
  uniform,
  vec3,
} from "three/tsl";
import * as THREE from "three/webgpu";
import { GameEngine } from "~/engine/instance";

export type ParticleEmitterOptions = {
  maxParticles: number;
  shape: "square" | "cube" | "point";
  gravity: number;
  angleVariance: number; // radians
  offsetVariance: number; // offset from emitter position
  lifetime: number; // seconds
  size: number;
};

function buildUniforms(options: ParticleEmitterOptions) {
  return {
    emitterPosition: uniform(vec3(0, 0, 0)),
    emitterVelocity: uniform(vec3(0, 0, 0)),
    angleVariance: uniform(options.angleVariance),
    offsetVariance: uniform(options.offsetVariance),
    velocityDamping: uniform(0.98),
    gravity: uniform(vec3(0, -options.gravity, 0)),
    decayFrequency: uniform(1.0 / options.lifetime),
    size: uniform(options.size),
    fadeIn: uniform(0.1),
    fadeOut: uniform(0.3),
    opacity: uniform(1.0),
    throughput: uniform(0), // [0,1], 0 = not emitting, 1 = max throughput
    emissionRate: uniform(100), // particles per second
    lastEmissionTime: uniform(0), // time tracking for emission
    lifetime: uniform(options.lifetime),
    emissionColor: uniform(color(1, 1, 1)),
  };
}

export class ParticleEmitter {
  private geometry: THREE.BufferGeometry;
  private material:
    | THREE.SpriteNodeMaterial
    | THREE.PointsNodeMaterial
    | THREE.MeshStandardMaterial
    | THREE.MeshBasicNodeMaterial;
  private mesh: THREE.InstancedMesh;
  private count: number;

  // Storage buffers
  private positionBuffer: THREE.StorageBufferNode<"vec3">;
  private velocityBuffer: THREE.StorageBufferNode<"vec3">;
  private lifeBuffer: THREE.StorageBufferNode<"float">;
  private colorBuffer: THREE.StorageBufferNode<"vec3">;

  // Compute shaders
  private particlesInitCompute: THREE.ComputeNode;
  private particlesUpdateCompute: THREE.ComputeNode;

  // Uniforms
  private uniforms: ReturnType<typeof buildUniforms>;

  constructor(options: ParticleEmitterOptions) {
    this.count = options.maxParticles;

    // Setup uniforms
    this.uniforms = buildUniforms(options);

    // Material
    let geometry, material;
    switch (options.shape) {
      case "square": {
        geometry = new THREE.PlaneGeometry(1, 1);
        material = new THREE.SpriteNodeMaterial({
          transparent: false,
          blending: THREE.NormalBlending,
          depthWrite: false,
        });
        break;
      }
      case "point": {
        geometry = new THREE.CircleGeometry(1, 8); // TODO: use faster point geometry?
        material = new THREE.PointsNodeMaterial({
          transparent: false,
          blending: THREE.NormalBlending,
          depthWrite: false,
        });
        break;
      }
      case "cube": {
        geometry = new THREE.BoxGeometry(
          options.size,
          options.size,
          options.size,
        );
        // TODO: 3D face coloring is not working
        // TODO: needs randomized rotation
        material = new THREE.MeshBasicNodeMaterial({
          transparent: false,
          blending: THREE.NormalBlending,
          depthWrite: false,
        });
        break;
      }
      default: {
        throw new Error(`Invalid particle shape: ${String(options.shape)}`);
      }
    }

    // const positionBuffer = new THREE.InstancedBufferAttribute(

    // )
    // const positionBufferStorage = storage()

    // Storage buffers
    this.positionBuffer = instancedArray(this.count, "vec3");
    this.velocityBuffer = instancedArray(this.count, "vec3");
    this.lifeBuffer = instancedArray(this.count, "float");
    this.colorBuffer = instancedArray(this.count, "vec3");

    // Compute init
    this.particlesInitCompute = Fn(() => {
      // Position
      const position = this.positionBuffer.element(instanceIndex);
      position.assign(this.uniforms.emitterPosition); // Set to emitter position

      // Life - stagger particles so they emit continuously
      const life = this.lifeBuffer.element(instanceIndex);
      // Spread particles evenly across the lifetime so they emit in sequence
      const staggeredLife = instanceIndex.toFloat().mul(2.0).div(this.count);
      life.assign(staggeredLife);

      // Color - initialize to white
      const colorElement = this.colorBuffer.element(instanceIndex);
      colorElement.assign(color(1, 1, 1));
    })().compute(this.count);

    // this.particlesInitCompute = particlesInit().compute(this.count);

    // Compute update
    this.particlesUpdateCompute = Fn(() => {
      // Setup
      const position = this.positionBuffer.element(instanceIndex);
      const velocity = this.velocityBuffer.element(instanceIndex);
      const life = this.lifeBuffer.element(instanceIndex);
      const colorElement = this.colorBuffer.element(instanceIndex);

      // Update velocity
      velocity.addAssign(this.uniforms.gravity.mul(deltaTime));
      velocity.mulAssign(this.uniforms.velocityDamping);

      // Update position
      position.addAssign(velocity.mul(deltaTime));

      // Life
      const newLife = life.add(deltaTime.mul(this.uniforms.decayFrequency));

      // Global timer-based emission for smooth cadence
      const emissionInterval = float(1)
        .div(float(this.count))
        .mul(this.uniforms.lifetime)
        .div(this.uniforms.throughput); // Scale interval by throughput
      const globalPhase = time.div(emissionInterval).floor();
      const particlePhase = instanceIndex.toFloat();
      const shouldRespawn = globalPhase
        .mod(float(this.count))
        .equal(particlePhase);

      // Only reset if emitting and it's this particle's turn
      If(newLife.greaterThan(1), () => {
        If(this.uniforms.throughput.greaterThan(0).and(shouldRespawn), () => {
          const randomAngleDelta = hash(instanceIndex)
            .sub(0.5)
            .mul(2.0)
            .mul(this.uniforms.angleVariance);

          // Apply random angle delta to velocity vector
          const baseVel = this.uniforms.emitterVelocity;
          const cosAngle = randomAngleDelta.cos();
          const sinAngle = randomAngleDelta.sin();

          // Rotate velocity vector by randomAngleDelta (2D rotation in XY plane)
          const newVelocity = vec3(
            baseVel.x.mul(cosAngle).sub(baseVel.y.mul(sinAngle)),
            baseVel.x.mul(sinAngle).add(baseVel.y.mul(cosAngle)),
            baseVel.z,
          );

          velocity.assign(newVelocity);

          // Position - create a new vec3 instead of modifying uniform
          const basePosition = this.uniforms.emitterPosition;
          const randomPositionOffset = vec3(
            hash(instanceIndex.add(uint(4)))
              .sub(0.5)
              .mul(2.0)
              .mul(this.uniforms.offsetVariance),
            hash(instanceIndex.add(uint(5)))
              .sub(0.5)
              .mul(2.0)
              .mul(this.uniforms.offsetVariance),
            0,
          );
          const newPosition = basePosition.add(randomPositionOffset);
          position.assign(newPosition);

          // Assign color from emissionColor uniform
          colorElement.assign(this.uniforms.emissionColor);
          life.assign(0); // Reset life to 0 for new particle
        });
      });

      // Always update life, but clamp to 1 if not emitting and dead
      If(this.uniforms.throughput.equal(0).and(newLife.greaterThan(1)), () => {
        life.assign(1); // Clamp to dead
      });
      If(
        this.uniforms.throughput.notEqual(0).or(newLife.lessThanEqual(1)),
        () => {
          life.assign(newLife);
        },
      );
    })().compute(this.count);

    // Scale (for SpriteNodeMaterial)
    const life = this.lifeBuffer.toAttribute();
    const scaleIn = life.remap(0, this.uniforms.fadeIn, 0, 1);
    const scaleOut = life.remap(this.uniforms.fadeOut.oneMinus(), 1, 1, 0);
    const scaleFinal = min(scaleIn, scaleOut)
      .smoothstep(0, 1)
      .mul(this.uniforms.size);
    if ("scaleNode" in material) {
      material.scaleNode = scaleFinal;
    } else {
      // geometry.setAttribute()
    }

    // Position
    material.positionNode = this.positionBuffer.toAttribute();
    // if ("positionNode" in material) {
    // } else {
    //   // set it on the geometry
    //   geometry.setAttribute("position", this.positionBuffer.value);
    // }

    // Per-particle color
    if ("colorNode" in material) {
      material.colorNode = this.colorBuffer.toAttribute();
      // this.material.colorNode = Fn(() => {
      //   // const currentLife = this.lifeBuffer.toAttribute();
      //   // const fade = currentLife.oneMinus(); // alpha = 1 - life
      //   const colorAttr = this.colorBuffer.toAttribute();
      //   return vec4(
      //     colorAttr.x,
      //     colorAttr.y,
      //     colorAttr.z,
      //     1,
      //     // fade.mul(this.uniforms.opacity),
      //   );
      // })();
    } else {
      geometry.setAttribute("color", this.colorBuffer.value);
    }

    this.geometry = geometry;
    this.material = material;
    this.mesh = new THREE.InstancedMesh(geometry, material, this.count);
    this.mesh.renderOrder = 9999;

    this.mesh.frustumCulled = false;

    // this.mesh.instanceMatrix.

    this.renderRegistration = GameEngine.renderer.onRender(this.update);
  }

  private renderRegistration?: { unregister: () => void };

  get object3d() {
    return this.mesh;
  }

  startEmitting(
    fromPosition: THREE.Vector3,
    velocity: THREE.Vector3,
    color: THREE.Color,
    throughput: number,
  ) {
    this.uniforms.emitterPosition.value.copy(fromPosition);
    this.uniforms.emitterVelocity.value.copy(velocity);
    this.uniforms.emissionColor.value.copy(color);
    this.uniforms.throughput.value = throughput;
  }

  stopEmitting() {
    this.uniforms.throughput.value = 0; // Stop emitting new particles
  }

  private initialComputed = false;

  /** Compute update. this should be called in the animation loop */
  private update = (renderer: THREE.WebGPURenderer) => {
    if (!this.initialComputed) {
      void renderer.compute(this.particlesInitCompute);
      this.initialComputed = true;
    }

    void renderer.compute(this.particlesUpdateCompute);
  };

  dispose() {
    this.renderRegistration?.unregister();

    this.mesh.removeFromParent();
    this.geometry.dispose();
    this.material.dispose();

    this.positionBuffer.dispose();
    this.velocityBuffer.dispose();
    this.lifeBuffer.dispose();
    this.colorBuffer.dispose();

    this.particlesInitCompute.dispose();
    this.particlesUpdateCompute.dispose();
  }
}
